// ZUSTAND→ATOM off-ramp (docs/projects/complete/web/zustand-to-effect-atom-migration.md)
// Status: not-started
// Target: Atom.make + Atom.fn actions on the app runtime
import { readyAudioStatuses } from '@listening/api-contract/audio-statuses';
import type { AudioItemSummary } from '@listening/api-contract/legacy-client-types';
import type { DocumentType } from '@listening/api-contract/schemas';
import {
  type PlaybackProgress,
  savePlaybackProgress,
} from '@listening/frontend/audio-chunk-player/progress-write';
import {
  type AudioItem,
  AudioItems,
  type CreateAudioByUrlPayload,
} from '@listening/frontend/query/audio-items';
import {
  type AudioList,
  continueListeningAtom,
  libraryListAtom,
} from '@listening/frontend/query/audio-lists';
import {
  invalidateAudioLists,
  syncAudio,
} from '@listening/frontend/query/audio-store';
import { apiMutation, idleQuery } from '@listening/frontend/query/api-runtime';
import { resolveAtom } from '@listening/frontend/query/load-atom';
import { getLibrarySortMode } from '@listening/frontend/stores/library-sort-store';
import { pinContinueListening } from '@/components/web-fe/design-v2/library-sort';
import { deleteAudioAtom } from '@listening/frontend/query/audio-mutations';
import {
  Effect,
  Exit,
  Schema,
  SchemaTransformation,
  Struct,
  Predicate,
} from 'effect';
import { FetchHttpClient } from 'effect/http';
import { getRuntime } from '@/runtimes/get-runtime';
import { formatTimeSeconds } from '@listening/utils/ts-utils';

import { useAudioPrefsStore } from '@listening/frontend/stores/audio-preferences-store';
import { useMatchRoute, useNavigate } from '@tanstack/react-router';
import { useAtom, useAtomSet, useAtomValue } from '@effect/atom-react';
import { AsyncResult, Atom, AtomRegistry } from 'effect/reactivity';
import { atomKvsSync } from '@listening/frontend/atoms/kvs';
import { AudioConversionSummary } from '@listening/api-contract/schemas';
import {
  useAudioChunkPlayer,
  useAudioChunkSelector,
  useDocumentSummary,
} from '@/lib/audio-chunk-player';
import { rootLogger } from '@/lib/root-logger';
import { useAuthSession } from '@/services/auth-service';
import { useFreePlanPrivateAudioLimit } from './subscription-service';
import { clientEnv } from '@/env';
import { useConversionPipelinePreferenceStore } from '@/stores/conversion-pipeline-preference';
import { conversionErrorMessage } from '@/lib/conversion-error-message';
import * as m from '@/paraglide/messages';

const moduleLogger = rootLogger.child({
  module: 'AudioService',
});

export type CreateAudioInput = {
  documentUrl: string;
  documentType?: DocumentType | undefined;
  title?: string | undefined;
  folderId?: string | null | undefined;
};

function normalizeCreateAudioInput(input: string | CreateAudioInput) {
  return Predicate.isString(input) ? { documentUrl: input } : input;
}

/** The detail of the audio the player has loaded. */
const useCurrentAudio = () => useDocumentSummary().documentSummary;

const useLoadAudioFromProgress = () =>
  useAudioChunkPlayer().loadAudioFromProgress;

function processingItemStatusString(audio: AudioItemSummary) {
  if (audio.conversion_status === 'initializing')
    return m.conversion_status_initializing();
  if (audio.conversion_status === 'completedExtraction')
    return m.conversion_status_generating_audio();
  if (audio.conversion_status === 'complete') return m.common_completed();
  if (audio.conversion_status === 'deleted')
    return m.conversion_status_deleted();
  if (audio.conversion_status === 'processingPDF')
    return m.conversion_status_processing_pdf();
  return conversionErrorMessage(audio);
}

const useContinueListeningAudioItems = (): ReadonlyArray<AudioItemSummary> => {
  // Ordering/filtering logic lives in `selectContinueListening` (shared query
  // layer) so web / RN / browser-ext derive it identically — see that helper
  // for the legacy-parity rationale (played_at = `progress.updated_at_ms`).
  //
  // Non-suspense + auth-gated: PlayBar mounts on the public `/doc/$docId` SSR
  // route, where the user is unauthenticated and the list endpoint 401s.
  // Suspense would block hydration (and spam Unauthorized errors). On `/app/*`
  // the home loader prefetches the continue-listening list, so the non-suspense
  // read resolves warm; the player just lists nothing for signed-out visitors.
  const { user } = useAuthSession();
  return AsyncResult.getOrElse(
    useAtomValue(user ? continueListeningAtom : idleQuery),
    () => [],
  );
};

// Rows go through the FULL contract schema (not typeSchema): its encoded
// side is the lenient one — rows persisted before a later-added optionalWith
// field still decode, instead of one old row fail-opening the whole cache.
// The transform's target is the TYPE-side identity so rows are
// decoded/encoded exactly once, by the union members.
const sessionConversionHistoryStateSchema = Schema.Struct({
  history: Schema.Array(AudioConversionSummary),
});
type SessionConversionHistoryState =
  typeof sessionConversionHistoryStateSchema.Type;
const emptySessionConversionHistory: SessionConversionHistoryState = {
  history: [],
};

// zustand-persist wrote a `{state, version}` envelope; decode merges over the
// empty default, encode writes the bare shape. See plan doc §C2.
const partialSessionConversionHistorySchema =
  sessionConversionHistoryStateSchema.mapFields(Struct.map(Schema.optionalKey));
const sessionConversionHistoryAtom = atomKvsSync({
  key: 'session-conversion-history',
  schema: Schema.Union([
    Schema.Struct({
      state: partialSessionConversionHistorySchema,
      version: Schema.Int,
    }),
    partialSessionConversionHistorySchema,
  ]).pipe(
    Schema.decodeTo(
      Schema.toType(sessionConversionHistoryStateSchema),
      SchemaTransformation.transform({
        decode: (stored) => ({
          ...emptySessionConversionHistory,
          ...('state' in stored ? stored.state : stored),
        }),
        encode: (state) => state,
      }),
    ),
  ),
  defaultValue: () => emptySessionConversionHistory,
});

const useSessionConversionHistoryStore = () => {
  const history = useAtomValue(sessionConversionHistoryAtom).history;
  const setState = useAtomSet(sessionConversionHistoryAtom);
  return {
    history,
    setHistory: (history: ReadonlyArray<AudioItemSummary>) => {
      setState({ history });
    },
  };
};

export type CreateAudioRequest = {
  readonly payload: CreateAudioByUrlPayload;
  /** Free plan: new audios default to public so they count against the free
   * public-audio quota rather than the paid private one. */
  readonly makePublic: boolean;
};

/**
 * Creates an audio from an uploaded or linked document. No optimistic insert:
 * the server assigns the id, and once it answers every list refetches so the
 * in-progress audio shows where the server places it.
 */
export const createAudioEffect = (request: CreateAudioRequest) =>
  Effect.gen(function* () {
    const created = yield* AudioItems.createByUrlEffect(request.payload);
    yield* invalidateAudioLists;
    const audio = request.makePublic
      ? yield* AudioItems.updateEffect(created.id, {
          visibility: 'public',
        }).pipe(
          Effect.tap((updated) => syncAudio(created.id, updated)),
          Effect.catch((error) =>
            Effect.logWarning(
              'Failed to make newly-created audio public',
              error,
            ).pipe(Effect.as(created)),
          ),
        )
      : created;
    yield* Effect.logInfo('Successfully requested convert url', {
      documentUrl: request.payload.document_url,
    });
    yield* Atom.update(sessionConversionHistoryAtom, (state) => ({
      history: [...state.history, audio],
    }));
    return audio;
  });

const createAudioAtom = apiMutation(createAudioEffect);

export const useConversionPipelineOverrides = () => {
  // Dev/staging-only pipeline override; never sent in production (flag off there).
  const pipelinePreference = useConversionPipelinePreferenceStore(
    (s) => s.preference,
  );
  const conversionPipelineVersion =
    clientEnv.conversionPipelineToggleEnabled &&
    pipelinePreference !== 'default'
      ? pipelinePreference
      : undefined;
  // Dev/staging-only vision override; only sent when the flag is on AND the user forced a value
  // ('on'/'off'). 'default' (or prod, where the flag is off) sends undefined so the server config
  // decides — v2 defaults vision off. 'on' sends false to force vision back on for a v2 test.
  const visionPreference = useConversionPipelinePreferenceStore(
    (s) => s.vision,
  );
  const conversionVisionOff =
    !clientEnv.conversionPipelineToggleEnabled || visionPreference === 'default'
      ? undefined
      : visionPreference === 'off';
  // Dev/staging-only v2 variant switches, same semantics: only sent when the flag is on AND the
  // user forced a non-default value. All are no-ops on a v1 conversion.
  const ocrProviderPreference = useConversionPipelinePreferenceStore(
    (s) => s.ocrProvider,
  );
  const conversionOcrProvider =
    clientEnv.conversionPipelineToggleEnabled &&
    ocrProviderPreference !== 'default'
      ? ocrProviderPreference
      : undefined;
  return {
    ...(conversionPipelineVersion === undefined
      ? {}
      : { conversion_pipeline_version: conversionPipelineVersion }),
    ...(conversionVisionOff === undefined
      ? {}
      : { conversion_vision_off: conversionVisionOff }),
    ...(conversionOcrProvider === undefined
      ? {}
      : { conversion_ocr_provider: conversionOcrProvider }),
  };
};

/** The create request for a document, carrying the viewer's plan and the
 * dev/staging pipeline overrides. */
export const useCreateAudioRequest = () => {
  const { limitEnabled } = useFreePlanPrivateAudioLimit();
  const overrides = useConversionPipelineOverrides();
  return (input: string | CreateAudioInput): CreateAudioRequest => {
    const createAudioInput = normalizeCreateAudioInput(input);
    return {
      payload: {
        document_url: createAudioInput.documentUrl,
        ...(createAudioInput.documentType === undefined
          ? {}
          : { document_type: createAudioInput.documentType }),
        ...(createAudioInput.title === undefined
          ? {}
          : { title: createAudioInput.title }),
        ...(createAudioInput.folderId === undefined
          ? {}
          : { folder_id: createAudioInput.folderId }),
        ...overrides,
      },
      makePublic: limitEnabled,
    };
  };
};

const useCreateAudioMutation = () => {
  const toRequest = useCreateAudioRequest();
  const [result, create] = useAtom(createAudioAtom, { mode: 'promiseExit' });
  return {
    createAudio: (input: string | CreateAudioInput) => create(toRequest(input)),
    isPending: result.waiting,
  };
};

/** Deletes an audio (it leaves every view at once), then unloads it from the
 * player and leaves its read-along page if either shows it. */
const useDeleteAudioMutation = () => {
  const navigate = useNavigate();
  const matchRoute = useMatchIsReadAlongScreenActiveForAudio();

  const { unloadAudio } = useAudioChunkPlayer();
  const loadedAudioId = useAudioChunkSelector((s) => s.audioId);
  const [result, deleteAudio] = useAtom(deleteAudioAtom, {
    mode: 'promiseExit',
  });

  const deleteAudioItem = async (audioItem: AudioItemSummary) => {
    const exit = await deleteAudio(audioItem.id);
    if (Exit.isSuccess(exit)) {
      moduleLogger.info('Deleted audio item successfully');
      if (loadedAudioId === audioItem.id) {
        unloadAudio();
      }
      if (matchRoute(audioItem)) {
        moduleLogger.info('Navigating to home screen from deleted audio');
        await navigate({ to: '/app/home' });
      }
    }
    return exit;
  };
  return { deleteAudio: deleteAudioItem, isPending: result.waiting };
};

const useMatchIsReadAlongScreenActiveForAudio = () => {
  const matchRoute = useMatchRoute();
  return (opts: {
    id: string;
    title?: string | null;
    url_slug?: string | null;
  }) => {
    const matchReadAlongRoute = (val: string) =>
      matchRoute({
        to: '/app/readalong/$audioIdOrSlug',
        params: { audioIdOrSlug: val },
        fuzzy: true,
      }) as boolean;

    if (opts.url_slug) {
      if (matchReadAlongRoute(opts.url_slug)) return true;
      // Also try the publicId suffix alone for backwards compat
      const publicId = opts.url_slug.split('-').at(-1);
      if (publicId && matchReadAlongRoute(publicId)) return true;
    }
    return matchReadAlongRoute(opts.id);
  };
};

const saveProgress = async (progress: PlaybackProgress) => {
  const saved = await getRuntime().runPromise(
    savePlaybackProgress(progress).pipe(
      // keepalive lets a save sent while the page unloads outlive the page.
      Effect.provideService(FetchHttpClient.RequestInit, { keepalive: true }),
      Effect.as(true),
      // A progress write is fire-and-forget: a rejection must never
      // escape as an uncaught promise and crash the reader. Log it instead. The root
      // fix keeps audio.page_count in sync with the pack (materialize-chapters), so a
      // legitimate page is never rejected — a failure here now signals a genuine
      // client/anchor bug worth seeing, not something to clamp/hide.
      Effect.catch((error) =>
        Effect.logWarning('Failed to persist reading progress').pipe(
          Effect.annotateLogs({ error }),
          Effect.as(false),
        ),
      ),
    ),
  );
  moduleLogger.debug(
    {
      ...progress,
      time: formatTimeSeconds(progress.timestampMs / 1000),
    },
    'Progress updated',
  );
  return saved;
};

const progressFields = [
  'audioId',
  'chapterId',
  'ttsId',
  'timestampMs',
  'docPackPage',
  'docPackCharStart',
  'docPackCharEnd',
] as const;
const isSameProgress = (a: PlaybackProgress, b: PlaybackProgress | undefined) =>
  b !== undefined && progressFields.every((field) => a[field] === b[field]);

let progressToSave: PlaybackProgress | undefined;
let lastSavedProgress: PlaybackProgress | undefined;
let savingProgress: Promise<void> | undefined;

// One save at a time, latest position wins: two in flight can land out of
// order and move the resume point back. A position equal to the last saved
// one is skipped (closing a tab fires both visibilitychange and pagehide).
const postAudioProgress = (progress: PlaybackProgress) => {
  if (!savingProgress && isSameProgress(progress, lastSavedProgress)) {
    return Promise.resolve();
  }
  progressToSave = progress;
  savingProgress ??= (async () => {
    try {
      while (progressToSave) {
        const next = progressToSave;
        progressToSave = undefined;
        if (isSameProgress(next, lastSavedProgress)) continue;
        if (await saveProgress(next)) lastSavedProgress = next;
      }
    } finally {
      savingProgress = undefined;
    }
  })();
  return savingProgress;
};

// The skip controls / auto-advance need the current audio's neighbor in the
// library list. We start from the loaded library list (route loaders + the
// nav/library views keep it warm) and load more of it on demand — stopping the
// moment the neighbor is known. The common case (a recently-created audio
// already on page 1) costs zero network; an older audio, or a cold list (e.g.
// a directly-opened `/app/readalong/$id` link, which has no loader to prefetch
// the library), pages forward until found instead of silently failing.
//
// TODO(scaling): the proper fix is dedicated next/previous-by-id backend
// endpoints so a skip is a single O(1) keyset lookup — see
// docs/projects/backlog/backend/list-and-get-audio-endpoints-v3.md.
const findAudioNeighbor = (
  registry: AtomRegistry.AtomRegistry,
  audioId: string | undefined,
  offset: 1 | -1,
) =>
  Effect.gen(function* () {
    const sortMode = getLibrarySortMode();
    const listAtom = libraryListAtom(sortMode);
    const continueItems =
      sortMode === 'continue-first'
        ? AsyncResult.getOrElse(registry.get(continueListeningAtom), () => [])
        : [];

    // The neighbor among the rows loaded so far. `undefined` means "need
    // another page" (only ever returned while there is one), so the loop
    // terminates.
    const resolve = (list: AudioList): AudioItem | null | undefined => {
      const ordered = pinContinueListening(list.items, sortMode, continueItems);
      if (!audioId) {
        if (ordered.length > 0) return ordered[0];
        return list.hasNextPage ? undefined : null;
      }
      const idx = ordered.findIndex((a) => a.id === audioId);
      if (idx === -1) return list.hasNextPage ? undefined : null;
      const neighborIdx = idx + offset;
      if (neighborIdx < 0) return null;
      if (neighborIdx < ordered.length) return ordered[neighborIdx];
      return list.hasNextPage ? undefined : null;
    };

    // Held mounted while it pages, so a page it asks for is not dropped.
    yield* Effect.acquireRelease(
      Effect.sync(() => registry.mount(listAtom)),
      (release) => Effect.sync(release),
    );
    let neighbor = resolve(yield* resolveAtom(registry, listAtom));
    while (neighbor === undefined) {
      registry.set(listAtom, undefined);
      neighbor = resolve(
        yield* AtomRegistry.getResult(registry, listAtom, {
          suspendOnWaiting: true,
        }),
      );
    }
    return neighbor;
  }).pipe(Effect.scoped);

function findNextAudio({
  registry,
  audioId,
}: {
  registry: AtomRegistry.AtomRegistry;
  audioId?: string | undefined;
}) {
  return Effect.runPromise(findAudioNeighbor(registry, audioId, 1));
}

function findPreviousAudio({
  registry,
  audioId,
}: {
  registry: AtomRegistry.AtomRegistry;
  audioId?: string | undefined;
}) {
  return Effect.runPromise(findAudioNeighbor(registry, audioId, -1));
}

function useCurrentChapterSummary() {
  const documentSummary = useDocumentSummary().documentSummary;
  const chapterId = useAudioChunkSelector((s) => s.chapterId);

  return {
    currentChapter: documentSummary?.chapters.find((c) => c.id === chapterId),
    documentSummary,
  };
}

export const audioService = {
  constants: {
    readyAudioStatuses,
  },
  secondaryQueries: {
    useContinueListeningAudioItems,
  },
  mutations: {
    useDeleteAudioMutation,
    useCreateAudioMutation,
  },
  player: {
    postAudioProgress,
    useLoadAudioFromProgress,
  },
  store: {
    useCurrentAudio,
    useAudioPrefsStore,
    useCurrentChapterSummary,
    useSessionConversionHistoryStore,
  },
  utils: {
    findNextAudio,
    findPreviousAudio,
    processingItemStatusString,
    useMatchIsReadAlongScreenActiveForAudio,
  },
};
